/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CanvasContext } from 'components/upload-image/CanvasContext';
import {
  FONT_DEFAULT_SIZE,
  FONT_DEFAULT_FAMILY,
  FONT_DEFAULT_COLOR,
  DEFAULT_EMPTY_WIDTH,
  LINE_HEIGHT_MULT,
  DRAG_START_THRESHOLD,
} from 'constants/fonts';
import AddTextButton from 'components/tools/text/AddTextButton';
import FontSizeControl from 'components/tools/text/FontSizeControl';
import TextAlignControl from './text/TextAlignControl';
import FontStyleControl from './text/FontStyleControl';
import FontColorControl from './text/FontColorControl';
import FontFamilyControl from './text/FontFamilyControl';
import TextRotationControl from './text/TextRotationControl';
import TextEffectsControl from './text/TextEffectsControl';
import useTooltipSequence from 'components/useTooltipSequence';
import useRegisterTooltipTarget from 'components/useRegisterTooltipTarget';
import { TEXT_PANEL_TOOLTIPS } from 'constants/textPanelTooltips';
import FeatureTooltip from 'components/FeatureToolTip';

/* ──────────────────────────
   Text measurement utilities
   ────────────────────────── */
let _measureCanvas;
function getMeasureCtx() {
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
  return _measureCanvas.getContext('2d');
}
function makeFontString({
  sizePx,
  family,
  weight = 'normal',
  style = 'normal',
}) {
  const stylePart = style && style !== 'normal' ? `${style} ` : '';
  const weightPart = weight && weight !== 'normal' ? `${weight} ` : '';
  return `${stylePart}${weightPart}${sizePx}px ${family}`.trim();
}

function measureLinesMetrics(
  text,
  fontPx,
  fontFamily,
  weight = 'normal',
  style = 'normal',
) {
  const ctx = getMeasureCtx();
  ctx.font = makeFontString({
    sizePx: fontPx,
    family: fontFamily,
    weight,
    style,
  });

  const lines = String(text ?? '').split('\n');
  let maxWidth = 0;
  let maxDescent = 0;

  // Fallbacks (fonts without actualBoundingBox* support)
  const fallbackDescent = Math.ceil(fontPx * 0.28);

  for (let i = 0; i < lines.length; i++) {
    const m = ctx.measureText(lines[i]);
    maxWidth = Math.max(maxWidth, m.width || 0);

    const d =
      typeof m.actualBoundingBoxDescent === 'number'
        ? m.actualBoundingBoxDescent
        : fallbackDescent;

    maxDescent = Math.max(maxDescent, d || 0);
  }

  return {
    maxWidth: Math.ceil(maxWidth),
    // A tiny safety nudge helps with some script fonts / browser differences
    maxDescent: Math.ceil(maxDescent) + 2,
    lineCount: Math.max(1, lines.length),
  };
}

/* Padding inside dashed border */
const PAD_X = 8; // total L+R
const PAD_Y = 8; // total T+B

function computeSize(item) {
  const fontPx = item.fontSize ?? FONT_DEFAULT_SIZE;
  const family = item.fontFamily ?? FONT_DEFAULT_FAMILY;
  const weight = item.fontWeight ?? 'normal';
  const style = item.fontStyle ?? 'normal';
  const text = item.text ?? '';
  const lineH = Math.ceil(fontPx * LINE_HEIGHT_MULT);

  // Width + descent metrics
  const metrics = text
    ? measureLinesMetrics(text, fontPx, family, weight, style)
    : {
        maxWidth: DEFAULT_EMPTY_WIDTH,
        maxDescent: Math.ceil(fontPx * 0.28) + 2,
        lineCount: 1,
      };

  const contentW = Math.max(DEFAULT_EMPTY_WIDTH, metrics.maxWidth + 6);

  // ✅ Key fix: add extra descent padding so low-hanging glyphs never clip
  const descPad = metrics.maxDescent;

  const contentH = Math.max(
    lineH,
    Math.ceil(metrics.lineCount * lineH) + descPad,
  );

  return { w: contentW + PAD_X, h: contentH + PAD_Y, lineH, descPad };
}

/* Rects & scale helpers */
function getCanvasRectAndScale(canvas) {
  const rect = canvas.getBoundingClientRect();
  return {
    rect,
    scaleX: canvas.width / rect.width,
    scaleY: canvas.height / rect.height,
  };
}
function clientToCanvasPx(clientX, clientY, canvas) {
  const { rect, scaleX, scaleY } = getCanvasRectAndScale(canvas);
  if (
    clientX < rect.left ||
    clientX > rect.right ||
    clientY < rect.top ||
    clientY > rect.bottom
  ) {
    return null;
  }
  return {
    xPx: (clientX - rect.left) * scaleX,
    yPx: (clientY - rect.top) * scaleY,
  };
}

function canvasPxToOverlayCss({ xPx, yPx }, canvas, overlay) {
  // snap to nearest 0.5px to avoid tiny sub-pixel jumps
  const snap = (v) => Math.round(v * 2) / 2;

  // ✅ Preferred: layout metrics (stable even with CSS transforms / fit-to-container centering)
  const cssW = canvas.offsetWidth;
  const cssH = canvas.offsetHeight;

  if (cssW > 0 && cssH > 0) {
    const scaleX = canvas.width / cssW;
    const scaleY = canvas.height / cssH;

    if (
      Number.isFinite(scaleX) &&
      Number.isFinite(scaleY) &&
      scaleX > 0 &&
      scaleY > 0
    ) {
      const layoutLeft = canvas.offsetLeft || 0;
      const layoutTop = canvas.offsetTop || 0;

      return {
        leftCss: snap(layoutLeft + xPx / scaleX),
        topCss: snap(layoutTop + yPx / scaleY),
      };
    }
  }

  // Fallback: rect metrics (only if offsets aren't available)
  const rect = canvas.getBoundingClientRect();
  const oRect = overlay.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;

  return {
    leftCss: snap(rect.left - oRect.left + xPx / scaleX),
    topCss: snap(rect.top - oRect.top + yPx / scaleY),
  };
}

const getInitialTextRotation = (canvasRotationDeg) => {
  const r = (((Number(canvasRotationDeg) || 0) % 360) + 360) % 360;
  return r === 0 ? 0 : -r;
};

function normaliseDeg(deg) {
  let d = deg % 360;
  if (d < -180) d += 360;
  if (d > 180) d -= 360;
  return d;
}

function snapToRightAngle(deg, threshold = 8) {
  const snapped = Math.round(deg / 90) * 90;
  return Math.abs(deg - snapped) <= threshold ? snapped : deg;
}

function normaliseDeg360(deg) {
  return (((Number(deg) || 0) % 360) + 360) % 360;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function TextTool({ isActive }) {
  const {
    canvasRef,
    overlayRef,
    activeTool,
    textItems,
    setTextItems,
    setRedrawTrigger,
    redrawTrigger,
    scaleRef,
    hasAddedTextRef,
    inputRefs,
    canvasRectRef,
    originalCanvasWidthRef,
    textInsertPoint,
    setTextInsertPoint,
    canvasRotationDeg,
    saveHistory,
  } = useContext(CanvasContext);

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);

  const inputRef = useRef(null);
  const wrapperRefs = useRef([]);
  const lastPlacementRef = useRef(null);

  const dragIndexRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ dx: 0, dy: 0 });
  const lockViewportRef = useRef(false);
  const textEditHistorySavedRef = useRef(false);
  const initialEditTextRef = useRef('');

  const pendingDragRef = useRef({
    active: false,
    index: null,
    startX: 0,
    startY: 0,
  });
  const dragJustStartedRef = useRef(false);

  const rotateStateRef = useRef({
    active: false,
    index: null,
    startAngleRad: 0,
    startRotationDeg: 0,
    pointerId: null,
  });

  const resizeStateRef = useRef({
    active: false,
    index: null,
    startDist: 1,
    startFontSize: FONT_DEFAULT_SIZE,
    pointerId: null,
  });
  const selectedItem = selectedIndex != null ? textItems[selectedIndex] : null;

  /* TOOLTIP FEATURE */
  const fontFamilyRef = useRef(null);
  const fontColorRef = useRef(null);
  const textEffectsRef = useRef(null);

  const textTooltipSequence = useTooltipSequence(TEXT_PANEL_TOOLTIPS, {
    enabled: isActive && activeTool === 'text' && !!selectedItem,
    initialDelay: 500,
    stepDelay: 1800,
    visibilityThreshold: 0.8,
    visibilityDelay: 300,
  });
  useRegisterTooltipTarget(
    textTooltipSequence,
    'text-font-family',
    fontFamilyRef,
  );

  useRegisterTooltipTarget(textTooltipSequence, 'text-colour', fontColorRef);

  useRegisterTooltipTarget(textTooltipSequence, 'text-shadow', textEffectsRef);

  const updateTextItem = (index, patch, options = {}) => {
    if (options.saveHistory !== false) {
      saveHistory();
    }

    setTextItems((prev) =>
      prev.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    );
    setRedrawTrigger((n) => n + 1);
  };

  const saveTextEditHistoryOnce = () => {
    if (textEditHistorySavedRef.current) return;

    saveHistory();
    textEditHistorySavedRef.current = true;
  };

  const getInsertCanvasPoint = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    if (textInsertPoint && typeof textInsertPoint.canvasX === 'number') {
      return { x: textInsertPoint.canvasX, y: textInsertPoint.canvasY };
    }

    if (
      textInsertPoint &&
      typeof textInsertPoint.cssX === 'number' &&
      typeof textInsertPoint.cssY === 'number'
    ) {
      const sx = scaleRef.current?.x || 1;
      const sy = scaleRef.current?.y || 1;
      return { x: textInsertPoint.cssX * sx, y: textInsertPoint.cssY * sy };
    }

    return null;
  };

  const computeNextToClickCentre = ({ clickX, clickY, w, h, canvas }) => {
    // We store x/y as the *centre* of the wrapper.
    // To make the textarea appear right where the user clicked (instead of
    // slightly down/right), align the wrapper's top-left corner with the click.
    // Subtract half the internal padding so the caret feels closer to the click.
    let x = clickX + w / 2 - PAD_X / 2;
    let y = clickY + h / 2 - PAD_Y / 2;

    const halfW = w / 2;
    const halfH = h / 2;

    x = clamp(x, halfW, canvas.width - halfW);
    y = clamp(y, halfH, canvas.height - halfH);

    return { x, y };
  };

  useEffect(() => {
    if (!textItems || !textItems.length) return;
    let changed = false;
    const next = textItems.map((t) => {
      const base = {
        fontWeight:
          typeof t.fontWeight !== 'undefined' ? t.fontWeight : 'normal',
        fontStyle: typeof t.fontStyle !== 'undefined' ? t.fontStyle : 'normal',
        color: typeof t.color !== 'undefined' ? t.color : FONT_DEFAULT_COLOR,
        fontFamily:
          typeof t.fontFamily !== 'undefined'
            ? t.fontFamily
            : FONT_DEFAULT_FAMILY,
        rotation: typeof t.rotation !== 'undefined' ? t.rotation : 0,
        hasOutline: typeof t.hasOutline !== 'undefined' ? t.hasOutline : false,
        outlineColor:
          typeof t.outlineColor !== 'undefined' ? t.outlineColor : '#000000',
        outlineWidth:
          typeof t.outlineWidth !== 'undefined' ? t.outlineWidth : 2,

        hasShadow: typeof t.hasShadow !== 'undefined' ? t.hasShadow : false,
        shadowColor:
          typeof t.shadowColor !== 'undefined' ? t.shadowColor : '#000000',
        shadowBlur: typeof t.shadowBlur !== 'undefined' ? t.shadowBlur : 4,
        shadowOffsetX:
          typeof t.shadowOffsetX !== 'undefined' ? t.shadowOffsetX : 2,
        shadowOffsetY:
          typeof t.shadowOffsetY !== 'undefined' ? t.shadowOffsetY : 2,
        shadowOpacity:
          typeof t.shadowOpacity !== 'undefined' ? t.shadowOpacity : 1,
      };
      if (
        typeof t.fontWeight === 'undefined' ||
        typeof t.fontStyle === 'undefined' ||
        typeof t.color === 'undefined' ||
        typeof t.fontFamily === 'undefined' ||
        typeof t.rotation === 'undefined' ||
        typeof t.hasOutline === 'undefined' ||
        typeof t.outlineColor === 'undefined' ||
        typeof t.outlineWidth === 'undefined' ||
        typeof t.hasShadow === 'undefined' ||
        typeof t.shadowColor === 'undefined' ||
        typeof t.shadowBlur === 'undefined' ||
        typeof t.shadowOffsetX === 'undefined' ||
        typeof t.shadowOffsetY === 'undefined' ||
        typeof t.shadowOpacity === 'undefined' ||
        typeof t.width === 'undefined' ||
        typeof t.height === 'undefined'
      ) {
        changed = true;
        const { w, h } = computeSize({ ...t, ...base });
        return { ...t, ...base, width: t.width ?? w, height: t.height ?? h };
      }
      return t;
    });
    if (changed) setTextItems(next);
  }, [textItems, setTextItems]);

  const refreshCanvasMetrics = React.useCallback(() => {
    if (lockViewportRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvasRectRef.current = rect;
    scaleRef.current = {
      x: canvas.width / rect.width,
      y: canvas.height / rect.height,
    };
    setRedrawTrigger((p) => p + 1);
  }, [canvasRef, setRedrawTrigger, canvasRectRef, scaleRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!originalCanvasWidthRef.current && canvas)
      originalCanvasWidthRef.current = canvas.width;
  }, [canvasRef, originalCanvasWidthRef]);

  useEffect(() => {
    const rawCanvasDeg = Number(canvasRotationDeg) || 0;
    const canvasDeg = ((rawCanvasDeg % 360) + 360) % 360; // 0,90,180,270
    // const is90 = canvasDeg === 90 || canvasDeg === 270;
    // const is270 = canvasDeg === 270;

    inputRefs.current.forEach((el, index) => {
      if (!el) return;
      const item = textItems[index];
      if (!item) return;

      const { descPad } = computeSize(item);

      const itemDeg = Number(item.rotation ?? 0);
      const creationCanvasDeg = normaliseDeg360(item._creationCanvasDeg ?? 0);
      const isCreationEditingItem =
        editingIndex === index && Number.isFinite(item._creationCanvasDeg);

      const displayCompensationDeg = isCreationEditingItem
        ? -creationCanvasDeg
        : 0;

      const effectiveDeg =
        (((canvasDeg + itemDeg + displayCompensationDeg) % 360) + 360) % 360;
      const isQuarterTurn = effectiveDeg === 90 || effectiveDeg === 270;
      const isEffective270 = effectiveDeg === 270;

      el.style.boxSizing = 'border-box';
      el.style.paddingTop = `${PAD_Y / 2}px`;
      el.style.paddingBottom = `${PAD_Y / 2 + (!isQuarterTurn ? descPad : 0)}px`;
      el.style.paddingLeft = `${PAD_X / 2 + (isEffective270 ? descPad : 0)}px`;
      el.style.paddingRight = `${PAD_X / 2 + (!isEffective270 && isQuarterTurn ? descPad : 0)}px`;
    });
  }, [
    redrawTrigger,
    activeTool,
    textItems,
    inputRefs,
    canvasRotationDeg,
    editingIndex,
  ]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay || activeTool !== 'text') return;

    const handlePointerDown = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const target = e.target;
      if (
        target &&
        (target.tagName === 'TEXTAREA' ||
          target.closest('[data-text-wrapper="1"]'))
      )
        return;

      const insertPt = getInsertCanvasPoint();

      const cPt =
        insertPt ||
        (() => {
          const raw = clientToCanvasPx(e.clientX, e.clientY, canvas);
          return raw ? { x: raw.xPx, y: raw.yPx } : null;
        })();

      if (!cPt) return;

      const clickX = cPt.x;
      const clickY = cPt.y;

      const hitRadius = 20;
      for (let i = 0; i < textItems.length; i++) {
        const { x: tx, y: ty } = textItems[i];
        if (
          Math.abs(clickX - tx) < hitRadius &&
          Math.abs(clickY - ty) < hitRadius
        ) {
          setSelectedIndex(i);
          setEditingIndex(i);
          if (textInsertPoint) setTextInsertPoint(null);
          return;
        }
      }

      // Add new next-to-click (ONLY for the very first click-to-add)
      if (!hasAddedTextRef.current) {
        const base = {
          text: '',
          color: FONT_DEFAULT_COLOR,
          fontFamily: FONT_DEFAULT_FAMILY,
          fontSize: FONT_DEFAULT_SIZE,
          textAlign: 'left',
          fontWeight: 'normal',
          fontStyle: 'normal',
          rotation: getInitialTextRotation(canvasRotationDeg),
          hasOutline: false,
          outlineColor: '#000000',
          outlineWidth: 2,

          hasShadow: false,
          shadowColor: '#000000',
          shadowOpacity: 1,
          shadowBlur: 4,
          shadowOffsetX: 2,
          shadowOffsetY: 2,
        };
        const { w, h } = computeSize(base);

        const placed = computeNextToClickCentre({
          clickX,
          clickY,
          w,
          h,
          canvas,
        });

        const newItem = {
          id: crypto.randomUUID?.() ?? String(Date.now()),
          x: placed.x,
          y: placed.y,
          ...base,
          width: w,
          height: h,
        };

        saveHistory();

        setTextItems((prev) => {
          const next = [...prev, newItem];
          const idx = next.length - 1;

          lastPlacementRef.current = null;

          setTimeout(() => {
            setSelectedIndex(idx);
            setEditingIndex(idx);
          }, 0);
          return next;
        });

        hasAddedTextRef.current = true;

        if (textInsertPoint) setTextInsertPoint(null);
      }
    };

    overlay.addEventListener('pointerdown', handlePointerDown);
    return () => overlay.removeEventListener('pointerdown', handlePointerDown);
  }, [
    overlayRef,
    activeTool,
    textItems,
    setTextItems,
    hasAddedTextRef,
    canvasRef,
    textInsertPoint,
    setTextInsertPoint,
  ]);

  useEffect(() => {
    const p = lastPlacementRef.current;
    if (!p) return;
    const overlay = overlayRef.current;
    const canvas = canvasRef.current;
    const wrap = wrapperRefs.current[p.index];
    if (!overlay || !canvas || !wrap) return;

    const rect = wrap.getBoundingClientRect();
    const centerClientX = rect.left + rect.width / 2;
    const centerClientY = rect.top + rect.height / 2;

    const deltaClientX = p.clientX - centerClientX;
    const deltaClientY = p.clientY - centerClientY;

    if (Math.abs(deltaClientX) < 0.5 && Math.abs(deltaClientY) < 0.5) {
      lastPlacementRef.current = null;
      return;
    }

    const { scaleX, scaleY } = getCanvasRectAndScale(canvas);
    const deltaCanvasX = deltaClientX * scaleX;
    const deltaCanvasY = deltaClientY * scaleY;

    setTextItems((prev) => {
      const next = [...prev];
      const it = next[p.index];
      if (!it) return prev;
      next[p.index] = {
        ...it,
        x: (it.x ?? 0) + deltaCanvasX,
        y: (it.y ?? 0) + deltaCanvasY,
      };
      return next;
    });

    lastPlacementRef.current = null;
  }, [textItems]);

  useEffect(() => {
    if (activeTool !== 'text') {
      setEditingIndex(null);
      setSelectedIndex(null);

      rotateStateRef.current = {
        active: false,
        index: null,
        startAngleRad: 0,
        startRotationDeg: 0,
        pointerId: null,
      };

      resizeStateRef.current = {
        active: false,
        index: null,
        startDist: 1,
        startFontSize: FONT_DEFAULT_SIZE,
        pointerId: null,
      };
    }
  }, [activeTool]);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      initialEditTextRef.current = textItems[editingIndex]?.text || '';
      const textarea = inputRef.current;
      lockViewportRef.current = true;
      try {
        textarea.focus({ preventScroll: true });
      } catch {
        textarea.focus();
      }
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  }, [editingIndex]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target?.tagName || '').toUpperCase();
      const inField =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        e.target?.isContentEditable;
      if (inField || activeTool !== 'text') return;

      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedIndex !== null &&
        editingIndex === null
      ) {
        e.preventDefault();
        deleteTextAt(selectedIndex);
        return;
      }

      const isCmd = e.metaKey || e.ctrlKey;
      if (!isCmd || selectedIndex === null) return;
      const key = e.key.toLowerCase();
      if (key === 'b') {
        e.preventDefault();
        toggleBold();
      } else if (key === 'i') {
        e.preventDefault();
        toggleItalic();
      }
    };
    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedIndex, editingIndex, textItems, activeTool]);

  const recomputeAndUpdateSize = (index, nextPatch = {}, options = {}) => {
    const currentItem = textItems[index];
    if (!currentItem) return;

    const measured = computeSize({ ...currentItem, ...nextPatch });

    const patchChanged = Object.entries(nextPatch).some(
      ([key, value]) => !Object.is(currentItem[key], value),
    );

    const sizeChanged =
      !Object.is(currentItem.width, measured.w) ||
      !Object.is(currentItem.height, measured.h);

    if (!patchChanged && !sizeChanged) return;

    if (options.saveHistory !== false) {
      saveHistory();
    }

    setTextItems((prev) => {
      const next = [...prev];
      const it = next[index];
      if (!it) return prev;

      const nextMeasured = computeSize({ ...it, ...nextPatch });

      next[index] = {
        ...it,
        ...nextPatch,
        width: nextMeasured.w,
        height: nextMeasured.h,
      };

      return next;
    });

    setRedrawTrigger((n) => n + 1);
  };

  const handleInputChange = (e, index) => {
    const text = e.target.value;
    const initialText = initialEditTextRef.current || '';

    if (initialText.trim() !== '') {
      saveTextEditHistoryOnce();
    }

    recomputeAndUpdateSize(index, { text }, { saveHistory: false });
  };
  const handleInputBlur = () => {
    lockViewportRef.current = false;
    textEditHistorySavedRef.current = false;
    refreshCanvasMetrics();

    if (activeTool === 'text') {
      setEditingIndex(null);
      setRedrawTrigger((prev) => prev + 1);
    }
  };

  const deleteTextAt = (index) => {
    if (index == null || index < 0) return;
    if (inputRefs?.current && inputRefs.current[index])
      inputRefs.current.splice(index, 1);
    saveHistory();
    setTextItems((prev) => prev.filter((_, i) => i !== index));
    setSelectedIndex(null);
    setEditingIndex(null);
    setRedrawTrigger((v) => v + 1);
  };

  const currentAlign =
    selectedIndex != null && textItems[selectedIndex]?.textAlign
      ? textItems[selectedIndex].textAlign
      : 'left';

  const toggleBold = () => {
    if (selectedIndex == null) return;
    const item = textItems[selectedIndex];
    const next = (item.fontWeight ?? 'normal') === 'bold' ? 'normal' : 'bold';
    recomputeAndUpdateSize(selectedIndex, { fontWeight: next });
  };

  const toggleItalic = () => {
    if (selectedIndex == null) return;
    const item = textItems[selectedIndex];
    const next =
      (item.fontStyle ?? 'normal') === 'italic' ? 'normal' : 'italic';
    recomputeAndUpdateSize(selectedIndex, { fontStyle: next });
  };

  // const setRegular = () => {
  //   if (selectedIndex != null)
  //     recomputeAndUpdateSize(selectedIndex, {
  //       fontWeight: 'normal',
  //       fontStyle: 'normal',
  //     });
  // };

  // ─── Drag logic ─────────
  const startDrag = (e, index) => {
    if (!isActive) return;

    saveHistory();

    e.preventDefault();
    e.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const cPt = clientToCanvasPx(e.clientX, e.clientY, canvas);
    if (!cPt) return;

    const item = textItems[index];
    dragOffsetRef.current = { dx: cPt.xPx - item.x, dy: cPt.yPx - item.y };
    dragIndexRef.current = index;
    isDraggingRef.current = true;
    setDraggingIndex(index);
    setSelectedIndex(index);

    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  };

  const onTextPointerDown = (e, index) => {
    if (!isActive) return;
    if (editingIndex === index) return;
    e.preventDefault();
    e.stopPropagation();

    setSelectedIndex(index);

    pendingDragRef.current = {
      active: true,
      index,
      startX: e.clientX,
      startY: e.clientY,
    };
    dragJustStartedRef.current = false;

    window.addEventListener('pointermove', armDragMove, { passive: false });
    window.addEventListener('pointerup', armDragUp, { passive: false });
    window.addEventListener('pointercancel', armDragUp, { passive: false });
  };

  const armDragMove = (e) => {
    const p = pendingDragRef.current;
    if (!p.active) return;
    const dx = e.clientX - p.startX,
      dy = e.clientY - p.startY;
    if (
      !dragJustStartedRef.current &&
      (Math.abs(dx) > DRAG_START_THRESHOLD ||
        Math.abs(dy) > DRAG_START_THRESHOLD)
    ) {
      dragJustStartedRef.current = true;
      startDrag(e, p.index);
    }
    if (dragJustStartedRef.current) e.preventDefault();
  };

  const armDragUp = () => {
    const p = pendingDragRef.current;
    window.removeEventListener('pointermove', armDragMove);
    window.removeEventListener('pointerup', armDragUp);
    window.removeEventListener('pointercancel', armDragUp);

    if (!dragJustStartedRef.current && p.active) {
      setEditingIndex(p.index);
      setTimeout(() => {
        const el = inputRefs.current[p.index];
        if (el) el.focus();
      }, 0);
    }
    pendingDragRef.current = {
      active: false,
      index: null,
      startX: 0,
      startY: 0,
    };
    dragJustStartedRef.current = false;
  };

  const onDragMove = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const idx = dragIndexRef.current;
    if (idx == null) return;

    const cPt = clientToCanvasPx(e.clientX, e.clientY, canvas);
    if (!cPt) return;

    const { dx, dy } = dragOffsetRef.current;
    const newX = cPt.xPx - dx,
      newY = cPt.yPx - dy;

    setTextItems((prev) => {
      const next = [...prev];
      if (!next[idx]) return prev;
      next[idx] = { ...next[idx], x: newX, y: newY };
      return next;
    });
  };

  const endDrag = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    dragIndexRef.current = null;
    setDraggingIndex(null);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', endDrag);
    setRedrawTrigger((p) => p + 1);
  };

  // ─── Rotate handle logic ──────────────────────────────────────
  const beginRotate = (e, index) => {
    if (!isActive) return;
    if (activeTool !== 'text') return;

    saveHistory();

    e.preventDefault();
    e.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const item = textItems[index];
    if (!item) return;

    const cPt = clientToCanvasPx(e.clientX, e.clientY, canvas);
    if (!cPt) return;

    setEditingIndex(null);
    setSelectedIndex(index);

    const angle = Math.atan2(cPt.yPx - (item.y ?? 0), cPt.xPx - (item.x ?? 0));

    rotateStateRef.current = {
      active: true,
      index,
      startAngleRad: angle,
      startRotationDeg: Number(item.rotation ?? 0),
      pointerId: e.pointerId ?? null,
    };

    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', onRotateMove, { passive: false });
    window.addEventListener('pointerup', endRotate, { passive: false });
    window.addEventListener('pointercancel', endRotate, { passive: false });
  };

  const onRotateMove = (e) => {
    const s = rotateStateRef.current;
    if (!s.active) return;

    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const idx = s.index;
    const item = textItems[idx];
    if (!item) return;

    const cPt = clientToCanvasPx(e.clientX, e.clientY, canvas);
    if (!cPt) return;

    const angle = Math.atan2(cPt.yPx - (item.y ?? 0), cPt.xPx - (item.x ?? 0));
    const deltaRad = angle - s.startAngleRad;
    const deltaDeg = (deltaRad * 180) / Math.PI;

    let nextDeg = normaliseDeg(s.startRotationDeg + deltaDeg);

    // snap to 0 / 90 / 180 / 270
    nextDeg = snapToRightAngle(nextDeg, 8);

    setTextItems((prev) => {
      const next = [...prev];
      if (!next[idx]) return prev;
      next[idx] = { ...next[idx], rotation: nextDeg };
      return next;
    });
  };

  const endRotate = () => {
    const s = rotateStateRef.current;
    if (!s.active) return;

    rotateStateRef.current = {
      active: false,
      index: null,
      startAngleRad: 0,
      startRotationDeg: 0,
      pointerId: null,
    };

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    window.removeEventListener('pointermove', onRotateMove);
    window.removeEventListener('pointerup', endRotate);
    window.removeEventListener('pointercancel', endRotate);

    setRedrawTrigger((p) => p + 1);
  };

  // ─── Resize handle logic (drag to scale fontSize) ──────────────
  const beginResize = (e, index) => {
    if (!isActive) return;
    if (activeTool !== 'text') return;

    saveHistory();

    e.preventDefault();
    e.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const item = textItems[index];
    if (!item) return;

    const cPt = clientToCanvasPx(e.clientX, e.clientY, canvas);
    if (!cPt) return;

    setEditingIndex(null);
    setSelectedIndex(index);

    const cx = item.x ?? 0;
    const cy = item.y ?? 0;

    const dx = cPt.xPx - cx;
    const dy = cPt.yPx - cy;
    const dist = Math.max(1, Math.hypot(dx, dy));

    resizeStateRef.current = {
      active: true,
      index,
      startDist: dist,
      startFontSize: Number(item.fontSize ?? FONT_DEFAULT_SIZE),
      pointerId: e.pointerId ?? null,
    };

    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', onResizeMove, { passive: false });
    window.addEventListener('pointerup', endResize, { passive: false });
    window.addEventListener('pointercancel', endResize, { passive: false });
  };

  const onResizeMove = (e) => {
    const s = resizeStateRef.current;
    if (!s.active) return;

    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const idx = s.index;
    const item = textItems[idx];
    if (!item) return;

    const cPt = clientToCanvasPx(e.clientX, e.clientY, canvas);
    if (!cPt) return;

    const cx = item.x ?? 0;
    const cy = item.y ?? 0;

    const dx = cPt.xPx - cx;
    const dy = cPt.yPx - cy;
    const dist = Math.max(1, Math.hypot(dx, dy));

    const scale = dist / s.startDist;
    const nextFontSize = clamp(Math.round(s.startFontSize * scale), 6, 220);

    setTextItems((prev) => {
      const next = [...prev];
      const it = next[idx];
      if (!it) return prev;

      const measured = computeSize({ ...it, fontSize: nextFontSize });

      next[idx] = {
        ...it,
        fontSize: nextFontSize,
        width: measured.w,
        height: measured.h,
      };
      return next;
    });
  };

  const endResize = () => {
    const s = resizeStateRef.current;
    if (!s.active) return;

    resizeStateRef.current = {
      active: false,
      index: null,
      startDist: 1,
      startFontSize: FONT_DEFAULT_SIZE,
      pointerId: null,
    };

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    window.removeEventListener('pointermove', onResizeMove);
    window.removeEventListener('pointerup', endResize);
    window.removeEventListener('pointercancel', endResize);

    setRedrawTrigger((p) => p + 1);
  };

  const overlayUI = (
    <>
      <div
        className="absolute inset-0"
        style={{
          zIndex: 0,
          pointerEvents: isActive ? 'auto' : 'none', // ✅ don't block other tools
        }}
        onPointerDown={(e) => {
          if (!isActive) return;

          // Optional: clicking empty space while in Text tool deselects text,
          // and stops the click from also placing stickers etc.
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(null);
          setEditingIndex(null);
        }}
      />
      {textItems.map((item, i) => {
        const measured = computeSize(item);
        const baseW = item.width ?? measured.w;
        const baseH = item.height ?? measured.h;

        const canvasDegRaw = Number(canvasRotationDeg) || 0;
        const canvasDegNorm = ((canvasDegRaw % 360) + 360) % 360;

        const itemDegRaw = Number(item.rotation ?? 0);
        const combinedDeg = canvasDegNorm + itemDegRaw;

        const effectiveDeg = ((combinedDeg % 360) + 360) % 360;

        const isQuarterTurn = effectiveDeg === 90 || effectiveDeg === 270;
        const isEffective270 = effectiveDeg === 270;

        let wCss = baseW;
        let hCss = baseH;

        const shouldInvertRotation =
          Boolean(item.flipX) !== Boolean(item.flipY);

        let deg = shouldInvertRotation ? -combinedDeg : combinedDeg;
        deg = ((deg % 360) + 360) % 360;
        const descPad = measured.descPad ?? 0;

        const overlay = overlayRef.current;
        const canvas = canvasRef.current;

        let leftCss = 0,
          topCss = 0;
        if (overlay && canvas) {
          const pos = canvasPxToOverlayCss(
            { xPx: item.x ?? 0, yPx: item.y ?? 0 },
            canvas,
            overlay,
          );

          leftCss = pos.leftCss;
          topCss = pos.topCss;
        }

        const isSelected =
          editingIndex === i || draggingIndex === i || selectedIndex === i;

        function hexToRgba(hex, alpha = 1) {
          const safe = (hex || '#000000').replace('#', '');
          const full =
            safe.length === 3
              ? safe
                  .split('')
                  .map((c) => c + c)
                  .join('')
              : safe.padEnd(6, '0').slice(0, 6);

          const r = parseInt(full.slice(0, 2), 16);
          const g = parseInt(full.slice(2, 4), 16);
          const b = parseInt(full.slice(4, 6), 16);

          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        const textShadowCss = item.hasShadow
          ? `${item.shadowOffsetX ?? 2}px ${item.shadowOffsetY ?? 2}px ${
              item.shadowBlur ?? 4
            }px ${hexToRgba(item.shadowColor ?? '#000000', item.shadowOpacity ?? 1)}`
          : 'none';

        const webkitStrokeWidth = item.hasOutline
          ? `${item.outlineWidth ?? 2}px`
          : '0px';

        const webkitStrokeColor = item.hasOutline
          ? (item.outlineColor ?? '#000000')
          : 'transparent';

        const isEmpty = !(item.text ?? '').length;
        return (
          <div
            key={item.id ?? i}
            data-text-wrapper="1"
            ref={(el) => (wrapperRefs.current[i] = el)}
            className={`group ${
              isSelected
                ? draggingIndex === i
                  ? 'border-2 border-red-500 shadow-md'
                  : 'border-2 border-sky-500 border-dashed shadow-sm'
                : 'border-2 border-transparent hover:border-stone-400 hover:border-dashed'
            }`}
            style={{
              position: 'absolute',
              left: `calc(${leftCss}px - ${wCss / 2}px)`,
              top: `calc(${topCss}px - ${hCss / 2}px)`,
              width: `${wCss}px`,
              height: `${hCss}px`,
              //transform: `rotate(${deg}deg)`,
              transform: `rotate(${deg}deg) scale(${item.flipX ? -1 : 1}, ${
                item.flipY ? -1 : 1
              })`,
              transformOrigin: 'center center',
              zIndex: isSelected ? 60 : 50,
              pointerEvents: isActive ? 'auto' : 'none',
              overflow: 'visible',
              background: 'transparent',
              boxSizing: 'border-box',
              willChange: 'transform,left,top',
            }}
            onPointerDown={(e) => onTextPointerDown(e, i)}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                overflow: 'visible',
                boxSizing: 'border-box',
                paddingLeft: `${PAD_X / 2 + (isEffective270 ? descPad : 0)}px`,
                paddingRight: `${PAD_X / 2 + (!isEffective270 && isQuarterTurn ? descPad : 0)}px`,
                paddingTop: `${PAD_Y / 2}px`,
                paddingBottom: `${PAD_Y / 2 + (!isQuarterTurn ? descPad : 0)}px`,
                fontSize: `${item.fontSize ?? FONT_DEFAULT_SIZE}px`,
                fontFamily: item.fontFamily ?? FONT_DEFAULT_FAMILY,
                fontWeight: item.fontWeight ?? 'normal',
                fontStyle: item.fontStyle ?? 'normal',
                lineHeight: LINE_HEIGHT_MULT,
                color: isEmpty
                  ? '#9ca3af' // same grey Tailwind uses for placeholder (text-gray-400)
                  : (item.color ?? FONT_DEFAULT_COLOR),
                whiteSpace: 'pre',
                wordBreak: 'normal',
                overflowWrap: 'normal',
                textAlign: item.textAlign,
                textShadow: textShadowCss,
                WebkitTextStrokeWidth: webkitStrokeWidth,
                WebkitTextStrokeColor: webkitStrokeColor,
                paintOrder: 'stroke fill',
                background: 'transparent',
                borderRadius: '4px',
                zIndex: 9,
              }}
            >
              {(item.text ?? '') || (editingIndex === i ? 'Start typing' : ' ')}
            </div>

            <textarea
              key={`${item.id ?? i}-${canvasDegNorm}`}
              data-text-index={i}
              ref={(elRef) => {
                inputRefs.current[i] = elRef;
                if (i === editingIndex) inputRef.current = elRef;
              }}
              value={item.text ?? ''}
              onChange={(e) => handleInputChange(e, i)}
              onBlur={handleInputBlur}
              readOnly={editingIndex !== i}
              autoFocus={i === editingIndex}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                transformOrigin: 'top left',
                paddingLeft: `${PAD_X / 2 + (isEffective270 ? descPad : 0)}px`,
                paddingRight: `${PAD_X / 2 + (!isEffective270 && isQuarterTurn ? descPad : 0)}px`,
                paddingTop: `${PAD_Y / 2}px`,
                paddingBottom: `${PAD_Y / 2 + (!isQuarterTurn ? descPad : 0)}px`,
                fontSize: `${item.fontSize ?? FONT_DEFAULT_SIZE}px`,
                fontFamily: item.fontFamily ?? FONT_DEFAULT_FAMILY,
                fontWeight: item.fontWeight ?? 'normal',
                fontStyle: item.fontStyle ?? 'normal',
                lineHeight: LINE_HEIGHT_MULT,
                cursor:
                  editingIndex === i
                    ? 'text'
                    : draggingIndex === i
                      ? 'grabbing'
                      : 'grab',
                touchAction: editingIndex === i ? 'auto' : 'none',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
                caretColor: item.color ?? FONT_DEFAULT_COLOR,
                border: '1px solid transparent',
                outline: 'none',
                boxShadow: 'none',
                margin: 0,
                backgroundColor: 'rgba(255,255,255,0)',
                borderRadius: '4px',
                zIndex: 10,
                resize: 'none',
                whiteSpace: 'pre',
                wordBreak: 'normal',
                overflowWrap: 'normal',
                boxSizing: 'border-box',
                textAlign: item.textAlign,
                overflow: 'hidden',
                textShadow: 'none',
                WebkitTextStrokeWidth: '0px',
                WebkitTextStrokeColor: 'transparent',
                paintOrder: 'stroke fill',
              }}
              placeholder="Start typing"
              onFocus={() => setSelectedIndex(i)}
            />

            {isActive && selectedIndex === i && (
              <button
                type="button"
                aria-label="Rotate text"
                onPointerDown={(e) => beginRotate(e, i)}
                className="absolute z-50 h-7 w-7 rounded-full bg-white/90 shadow hover:bg-white border border-gray-200 flex items-center justify-center"
                style={{
                  left: '50%',
                  top: '-42px',
                  transform: `translateX(-50%) rotate(${-deg}deg)`,
                  transformOrigin: 'center',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    fontSize: 24,
                    lineHeight: 1,
                    userSelect: 'none',
                    paddingBottom: '4px',
                  }}
                >
                  ⟳
                </span>
              </button>
            )}

            {isActive && selectedIndex === i && (
              <button
                type="button"
                aria-label="Resize text"
                onPointerDown={(e) => beginResize(e, i)}
                className="absolute z-50 h-6 w-6 rounded-full bg-white/90 shadow hover:bg-white border border-gray-200 flex items-center justify-center"
                style={{
                  right: '-16px',
                  bottom: '-16px',
                  transform: `rotate(${-deg}deg)`,
                  transformOrigin: 'center',
                  cursor: 'nwse-resize',
                  touchAction: 'none',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    fontSize: 14,
                    lineHeight: 1,
                    userSelect: 'none',
                  }}
                >
                  ↔
                </span>
              </button>
            )}

            {isActive && selectedIndex === i && (
              <button
                type="button"
                aria-label="Delete text"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteTextAt(i);
                }}
                className="absolute z-50 h-7 w-7 rounded-full bg-white/90 shadow hover:bg-white border border-gray-200 flex items-center justify-center"
                style={{
                  right: '-18px',
                  top: '-18px',
                  transform: `rotate(${-deg}deg)`,
                  transformOrigin: 'center',
                }}
              >
                <span className="p-1" role="img" aria-hidden>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/delete.svg`}
                    alt="Delete text"
                    className="w-[20px] h-[20px] mr-0"
                  />
                </span>
              </button>
            )}
          </div>
        );
      })}
    </>
  );

  const showInitialHint =
    isActive &&
    activeTool === 'text' &&
    !hasAddedTextRef.current &&
    (!textItems || textItems.length === 0);

  return (
    <div className="flex mb-4 text-sm">
      <div className="flex gap-4 flex-nowrap min-w-max items-center">
        {isActive &&
          (showInitialHint ? (
            <div className="px-3 py-2 rounded-xl text-sm text-stone-500">
              click anywhere on the image to add text
            </div>
          ) : (
            <AddTextButton
              onAdd={(index) => {
                hasAddedTextRef.current = true;

                const canvas = canvasRef.current;
                if (canvas) {
                  const base = {
                    text: '',
                    fontFamily: FONT_DEFAULT_FAMILY,
                    fontWeight: 'normal',
                    fontStyle: 'normal',
                    fontSize: FONT_DEFAULT_SIZE,
                    hasOutline: false,
                    outlineColor: '#000000',
                    outlineWidth: 2,

                    hasShadow: false,
                    shadowColor: '#000000',
                    shadowBlur: 4,
                    shadowOffsetX: 2,
                    shadowOffsetY: 2,
                  };
                  const { w, h } = computeSize(base);

                  // ✅ IMPORTANT: Add-more-text should ALWAYS centre (as before)
                  const placed = { x: canvas.width / 2, y: canvas.height / 2 };

                  updateTextItem(index, {
                    x: placed.x,
                    y: placed.y,
                    width: w,
                    height: h,
                    rotation: getInitialTextRotation(canvasRotationDeg),
                  });

                  // ✅ Also clear any remembered click so it can't influence future adds
                  if (textInsertPoint) setTextInsertPoint(null);
                }

                setSelectedIndex(index);
                setEditingIndex(index);
              }}
            />
          ))}

        {isActive && selectedItem && (
          <>
            <div ref={fontFamilyRef}>
              <FontFamilyControl
                value={selectedItem.fontFamily ?? FONT_DEFAULT_FAMILY}
                onChange={(family) =>
                  recomputeAndUpdateSize(selectedIndex, { fontFamily: family })
                }
              />
            </div>

            <FontSizeControl
              value={selectedItem.fontSize ?? FONT_DEFAULT_SIZE}
              onChange={(n) =>
                recomputeAndUpdateSize(selectedIndex, { fontSize: n })
              }
            />
            <TextAlignControl
              applyTextAlign={(align) =>
                updateTextItem(selectedIndex, { textAlign: align })
              }
              selectedIndex={selectedIndex}
              currentAlign={currentAlign}
            />
            <FontStyleControl
              selectedItem={selectedItem}
              setRegular={() => {
                if (selectedIndex != null)
                  recomputeAndUpdateSize(selectedIndex, {
                    fontWeight: 'normal',
                    fontStyle: 'normal',
                  });
              }}
              toggleBold={() => {
                if (selectedIndex == null) return;
                const item = textItems[selectedIndex];
                const next =
                  (item.fontWeight ?? 'normal') === 'bold' ? 'normal' : 'bold';
                recomputeAndUpdateSize(selectedIndex, { fontWeight: next });
              }}
              toggleItalic={() => {
                if (selectedIndex == null) return;
                const item = textItems[selectedIndex];
                const next =
                  (item.fontStyle ?? 'normal') === 'italic'
                    ? 'normal'
                    : 'italic';
                recomputeAndUpdateSize(selectedIndex, { fontStyle: next });
              }}
            />
            <div ref={fontColorRef}>
              <FontColorControl
                value={selectedItem.color ?? FONT_DEFAULT_COLOR}
                onChange={(hex) =>
                  updateTextItem(selectedIndex, { color: hex })
                }
              />
            </div>
            <div ref={textEffectsRef}>
              <TextEffectsControl
                hasOutline={selectedItem.hasOutline ?? false}
                outlineColor={selectedItem.outlineColor ?? '#000000'}
                outlineWidth={selectedItem.outlineWidth ?? 2}
                hasShadow={selectedItem.hasShadow ?? false}
                shadowColor={selectedItem.shadowColor ?? '#000000'}
                shadowBlur={selectedItem.shadowBlur ?? 4}
                shadowOffsetX={selectedItem.shadowOffsetX ?? 2}
                shadowOffsetY={selectedItem.shadowOffsetY ?? 2}
                shadowOpacity={selectedItem.shadowOpacity ?? 1}
                onChangeHasOutline={(hasOutline) =>
                  updateTextItem(selectedIndex, { hasOutline })
                }
                onChangeOutlineColor={(outlineColor) =>
                  updateTextItem(selectedIndex, { outlineColor })
                }
                onChangeOutlineWidth={(outlineWidth) =>
                  updateTextItem(selectedIndex, { outlineWidth })
                }
                onChangeHasShadow={(hasShadow) =>
                  updateTextItem(selectedIndex, { hasShadow })
                }
                onChangeShadowColor={(shadowColor) =>
                  updateTextItem(selectedIndex, { shadowColor })
                }
                onChangeShadowBlur={(shadowBlur) =>
                  updateTextItem(selectedIndex, { shadowBlur })
                }
                onChangeShadowOffsetX={(shadowOffsetX) =>
                  updateTextItem(selectedIndex, { shadowOffsetX })
                }
                onChangeShadowOffsetY={(shadowOffsetY) =>
                  updateTextItem(selectedIndex, { shadowOffsetY })
                }
                onChangeShadowOpacity={(shadowOpacity) =>
                  updateTextItem(selectedIndex, { shadowOpacity })
                }
              />
            </div>
            <TextRotationControl
              value={selectedItem.rotation ?? 0}
              onChange={(deg) =>
                updateTextItem(selectedIndex, { rotation: deg })
              }
            />
            {textTooltipSequence.activeTooltip && (
              <FeatureTooltip
                targetRef={textTooltipSequence.activeTooltip.ref}
                open={true}
                placement={
                  textTooltipSequence.activeTooltip.placement || 'right'
                }
                align={textTooltipSequence.activeTooltip.align || 'center'}
                title={textTooltipSequence.activeTooltip.title}
                description={textTooltipSequence.activeTooltip.description}
                buttonLabel="Got it"
                onClose={() =>
                  textTooltipSequence.closeTooltip(
                    textTooltipSequence.activeTooltip.id,
                  )
                }
              />
            )}
          </>
        )}

        {overlayRef.current && createPortal(overlayUI, overlayRef.current)}
      </div>
    </div>
  );
}
